import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { HEADING_SIZE, Heading } from '../../elements/Heading';
import { ReadMore } from '../../elements/ReadMore';
import { useTheme } from '../../../theme';
import { GROUP_TYPE, Group } from '../../elements/Group';
import { Box } from '../../elements/layout/Box';
import { Icon } from '../../elements/Icon';
import { Column, FONT, FONT_SIZE, Row, TYPOGRAPHY_TYPE } from '../../..';
import { COLOR } from '../../../theme/colors';
import { KEY_CODES } from '../../../utils/misc';
import { useHover } from '../../../utils/hooks/useHover';
export var CARD_LAYOUT;
(function (CARD_LAYOUT) {
    CARD_LAYOUT["VERTICAL"] = "vertical";
    CARD_LAYOUT["HORIZONTAL"] = "horizontal";
})(CARD_LAYOUT || (CARD_LAYOUT = {}));
export var CARD_SIZE;
(function (CARD_SIZE) {
    CARD_SIZE["SM"] = "SM";
    CARD_SIZE["MD"] = "MD";
    CARD_SIZE["LG"] = "LG";
})(CARD_SIZE || (CARD_SIZE = {}));
const MONTH_LABELS = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
];
const DateDisplay = ({ date: dateString }) => {
    const date = React.useMemo(() => {
        return new Date(dateString);
    }, [dateString]);
    return (_jsxs(Column, Object.assign({ sx: {
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            fontFamily: FONT.BRANDED2,
            backgroundColor: COLOR.ND_GOLD_LIGHT,
        } }, { children: [_jsx(Box, Object.assign({ sx: {
                    fontSize: FONT_SIZE.LG,
                } }, { children: date.getDate() })), _jsx(Box, Object.assign({ sx: {
                    fontSize: FONT_SIZE.MD,
                    mt: 1,
                } }, { children: MONTH_LABELS[date.getMonth()] }))] })));
};
export const Card = ({ size, displayDate, headline, image, alt, layout, onClick, raised, truncateHeadlineAfter, heading, headlineStyles, headingStyles, headingIcon, headingAction, backgroundColor, sx, imageHeight: imageHeightProp, imageWidth: imageWidthProp, containImage, containerWidth, containerHeight, children, }) => {
    var _a;
    const theme = useTheme();
    const contentPaddingX = size === CARD_SIZE.SM ? 3 : 4;
    const isVertical = !layout || layout === CARD_LAYOUT.VERTICAL;
    const { isHovered, anchorElementProps } = useHover();
    const [activeBackground, setActiveBackground] = useState(isHovered ? COLOR.ND_SKY_BLUE : COLOR.BACKGROUND);
    const [objectFit, setObjectFit] = useState('scale-down');
    useEffect(() => {
        setActiveBackground(isHovered ? COLOR.ND_SKY_BLUE : COLOR.BACKGROUND);
    }, [isHovered]);
    useEffect(() => {
        if (image) {
            const img = new Image();
            img.onload = () => {
                if (img.width > img.height) {
                    setObjectFit('contain');
                }
                else {
                    setObjectFit('scale-down');
                }
            };
            img.src = image;
        }
    }, [image]);
    const imageHeight = imageHeightProp || (isVertical ? 'auto' : '100%');
    const imageWidth = imageWidthProp || (isVertical ? '100%' : 'auto');
    const contentPaddingY = isVertical ? contentPaddingX : 2;
    const typography = size === CARD_SIZE.SM
        ? TYPOGRAPHY_TYPE.HEADLINE_SMALL
        : TYPOGRAPHY_TYPE.HEADLINE_MEDIUM;
    return (_jsxs(Group, Object.assign({}, anchorElementProps, { type: headline || heading ? GROUP_TYPE.REGION : GROUP_TYPE.GROUP, role: onClick ? 'button' : 'group', onClick: onClick, onKeyDown: (e) => {
            if (onClick && e.key === KEY_CODES.ENTER) {
                onClick();
            }
        }, tabIndex: onClick ? 0 : undefined, sx: Object.assign({ boxShadow: raised || heading ? (_a = theme.boxShadow) === null || _a === void 0 ? void 0 : _a.NORMAL : undefined, width: !isVertical && '100%', backgroundColor: backgroundColor ? backgroundColor : COLOR.WHITE, borderBottom: 'solid 2px', borderColor: COLOR.TRANSPARENT, cursor: onClick ? 'pointer' : undefined, display: 'flex', flexDirection: layout === CARD_LAYOUT.HORIZONTAL ? 'row' : 'column', alignItems: 'flex-start', ':hover,:focus': onClick
                ? {
                    transform: 'scale(1.01)',
                    borderColor: COLOR.ND_SKY_BLUE_DARK,
                    backgroundColor: COLOR.ND_SKY_BLUE,
                }
                : {} }, sx) }, { children: [image && !containImage && (_jsx("img", { src: image, alt: alt, style: {
                    width: imageWidth,
                    height: imageHeight,
                } })), image && containImage && (_jsx(Box, Object.assign({ sx: {
                    width: containerWidth || '500px',
                    height: containerHeight || '280px',
                    backgroundColor: COLOR.ND_SKY_BLUE,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                } }, { children: _jsx("img", { src: image, alt: alt, style: {
                        width: imageWidth,
                        height: imageHeight,
                        objectFit: objectFit,
                    } }) }))), displayDate && _jsx(DateDisplay, { date: displayDate }), heading && (_jsxs(Row, Object.assign({ sx: Object.assign({ bg: COLOR.PRIMARY, color: COLOR.WHITE, width: '100%', px: contentPaddingX, py: 3, justifyContent: 'space-between', alignItems: 'center' }, headingStyles) }, { children: [_jsxs(Row, { children: [headingIcon && (_jsx(Icon, { icon: headingIcon, size: FONT_SIZE.LG, color: COLOR.WHITE, sx: { mr: 2 } })), _jsx(Heading, Object.assign({ size: HEADING_SIZE.SM, sx: {
                                    color: COLOR.WHITE,
                                    mt: 0,
                                    whiteSpace: 'nowrap',
                                } }, { children: heading }))] }), _jsx(Row, { children: headingAction && headingAction })] }))), _jsx(Row, Object.assign({ sx: { px: contentPaddingX, py: contentPaddingY } }, { children: _jsxs(Column, Object.assign({ justify: "center" }, { children: [headline && (_jsx(Heading, Object.assign({ typography: typography, sx: Object.assign({ mt: 0 }, headlineStyles) }, { children: truncateHeadlineAfter ? (_jsx(ReadMore, Object.assign({ lines: truncateHeadlineAfter, typography: typography, sx: { bg: activeBackground } }, { children: headline }))) : (headline) }))), children && (_jsx(Box, Object.assign({ sx: { mt: headline && !heading ? 2 : 0 } }, { children: typeof children === 'function'
                                ? children({ isHovered, activeBackground })
                                : children })))] })) }))] })));
};
